/**
 * LLM Usage Analytics Dashboard
 *
 * Consumes subscription-analytics endpoints to show:
 *  - Summary cards (total cost, daily avg, cost/user, total calls)
 *  - Daily cost trend (area chart)
 *  - By-call-name table (sortable)
 *  - By-model breakdown (bar chart)
 *  - Actual vs Modeled comparison
 *  - By-profile comparison
 *
 * Period selector: 7 / 30 / 90 days
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { storage } from '../services/storage';
import { LLMHealthMonitor } from '../components/admin/LLMHealthMonitor';
import { AdminDateRangePicker } from '../components/admin/AdminDateRangePicker';
import { UnattributedBanner } from '../components/admin/UnattributedBanner';
import { useAdminDateRange } from '../hooks/useAdminDateRange';
import {
  DollarSign,
  Users,
  Brain,
  Calendar,
  TrendingUp,
  Activity,
  HelpCircle,
  ArrowUpDown,
  Layers,
  GitCompare,
  ShieldAlert,
  Scale,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  PiggyBank,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// ── API helper (mirrors AdminSubscriptionAnalytics) ──────────────────────────

import { API_BASE_URL } from '../utils/apiConfig';

async function apiFetch<T>(path: string): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');

  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface OverviewData {
  period_days: number;
  totals: {
    total_calls: number;
    unique_users: number;
    total_cost: number;
    avg_cost_per_call: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
    avg_response_time: number;
  };
  by_function: { function_id: number; function_name: string; call_count: number; total_cost: number; avg_cost: number; pct_of_total: number }[];
  by_tier: { tier: string; user_count: number; total_calls: number; total_cost: number; avg_cost_per_user: number }[];
  by_model: { model: string; call_count: number; total_cost: number; avg_cost_per_call: number; avg_input: number; avg_output: number }[];
  daily_trend: { day: string; calls: number; unique_users: number; cost: number; avg_cost: number }[];
}

interface CallNameRow {
  call_name: string;
  call_count: number;
  unique_users: number;
  total_cost: number;
  avg_cost: number;
  avg_input: number;
  avg_output: number;
  avg_latency: number;
  most_used_model: string;
  pct_of_total: number;
}

interface ByCallNameData {
  period_days: number;
  total_cost: number;
  call_names: CallNameRow[];
}

interface ComparisonRow {
  call_name: string;
  display_name: string;
  category: string;
  call_count: number;
  actual_model: string;
  modeled_model: string;
  avg_actual_cost: number;
  avg_input_tokens: number;
  avg_output_tokens: number;
  estimated_input_tokens: number;
  estimated_output_tokens: number;
}

interface ActualVsModeledData {
  period_days: number;
  comparisons: ComparisonRow[];
}

interface ProfileData {
  period_days: number;
  profiles: {
    profile_name: string;
    user_count: number;
    total_calls: number;
    total_cost: number;
    avg_cost_per_user: number;
    min_cost_per_user: number;
    max_cost_per_user: number;
    avg_cost_per_call: number;
    avg_latency: number;
    call_breakdown: { call_name: string; call_count: number; total_cost: number; avg_cost: number; most_used_model: string }[];
  }[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number, d = 2) => `$${n.toFixed(d)}`;
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtNum = (n: number) => n.toLocaleString();

const CHART_COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6'];

const HelpTooltip: React.FC<{ content: string }> = ({ content }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <HelpCircle
        className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help ml-1"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-10">
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          {content}
        </span>
      )}
    </span>
  );
};

// ── Tabs ─────────────────────────────────────────────────────────────────────

type TabKey = 'overview' | 'reconciliation' | 'cache' | 'by-call' | 'actual-vs-modeled' | 'by-profile' | 'health';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Activity className="h-4 w-4" /> },
  { key: 'reconciliation', label: 'Reconciliation', icon: <Scale className="h-4 w-4" /> },
  { key: 'cache', label: 'Cache Savings', icon: <PiggyBank className="h-4 w-4" /> },
  { key: 'health', label: 'Health Monitor', icon: <ShieldAlert className="h-4 w-4" /> },
  { key: 'by-call', label: 'By Call Name', icon: <Layers className="h-4 w-4" /> },
  { key: 'actual-vs-modeled', label: 'Actual vs Modeled', icon: <GitCompare className="h-4 w-4" /> },
  { key: 'by-profile', label: 'By Profile', icon: <Users className="h-4 w-4" /> },
];

// ── Main Component ──────────────────────────────────────────────────────────

type UserFilter = 'all' | 'external' | 'internal';

export const LLMUsageAnalytics: React.FC = () => {
  const { startDate, endDate, setRange, queryParams } = useAdminDateRange('30d');
  const [tab, setTab] = useState<TabKey>('overview');

  // User-set filter — defaults to 'external' so the page reflects real-customer
  // LLM costs (same set as Platform Analytics). 'all' includes testers/dev.
  // 'internal' shows ONLY testers/dev (useful for "what's our QA traffic costing").
  const [userFilter, setUserFilter] = useState<UserFilter>('external');

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [byCall, setByCall] = useState<ByCallNameData | null>(null);
  const [comparison, setComparison] = useState<ActualVsModeledData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortCol, setSortCol] = useState<keyof CallNameRow>('total_cost');
  const [sortAsc, setSortAsc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = `/api/admin/subscription-analytics`;
      const params = `${queryParams}&user_filter=${userFilter}`;
      const results = await Promise.all([
        apiFetch<OverviewData>(`${base}/overview?${params}`),
        apiFetch<ByCallNameData>(`${base}/by-call-name?${params}`),
        apiFetch<ActualVsModeledData>(`${base}/actual-vs-modeled?${params}`),
        apiFetch<ProfileData>(`${base}/by-profile?${params}`),
      ]);
      setOverview(results[0]);
      setByCall(results[1]);
      setComparison(results[2]);
      setProfileData(results[3]);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [queryParams, userFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Sort logic for by-call table ──
  const sortedCalls = byCall ? [...byCall.call_names].sort((a, b) => {
    const av = a[sortCol] ?? 0;
    const bv = b[sortCol] ?? 0;
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  }) : [];

  const toggleSort = (col: keyof CallNameRow) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };

  const SortHeader: React.FC<{ col: keyof CallNameRow; label: string; className?: string }> = ({ col, label, className }) => (
    <th
      className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 ${className ?? ''}`}
      onClick={() => toggleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortCol === col && <ArrowUpDown className="h-3 w-3" />}
      </span>
    </th>
  );

  // ── Loading / Error states ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        <span className="ml-3 text-gray-600">Loading LLM usage analytics...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={load} className="mt-2 text-red-600 hover:text-red-800 underline">Try again</button>
      </div>
    );
  }
  if (!overview) return null;

  const avgDaily = overview.daily_trend.length > 0
    ? overview.totals.total_cost / overview.daily_trend.length
    : 0;
  const costPerUser = overview.totals.unique_users > 0
    ? overview.totals.total_cost / overview.totals.unique_users
    : 0;

  // ── Render ──
  return (
    <div className="space-y-6">
      {/* Header + period selector + user filter */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center" style={{ fontFamily: "'League Spartan', sans-serif", color: '#0B1614' }}>
            <Brain className="h-7 w-7 text-green-600 mr-3" />
            LLM Usage Analytics
          </h1>
          <p className="text-gray-600 mt-1">
            Real cost data from llm_usage_actual.
            {' '}
            <span className="text-gray-500">
              {userFilter === 'external' && 'Showing external users only (real customers, same set as Platform Analytics).'}
              {userFilter === 'all' && 'Showing all users (including testers + dev accounts).'}
              {userFilter === 'internal' && 'Showing internal users only (testers + dev accounts).'}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* User-set filter — flows through to every endpoint as ?user_filter=...
              The set definitions are: external = v_analytics_included_users (the
              same dataset as Platform Analytics); internal = v_analytics_excluded_users;
              all = no filter. */}
          <label className="inline-flex items-center gap-2">
            <span className="text-sm text-gray-600">Users:</span>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value as UserFilter)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="external">External (real customers)</option>
              <option value="all">All users</option>
              <option value="internal">Internal (testers / dev)</option>
            </select>
          </label>
          <AdminDateRangePicker startDate={startDate} endDate={endDate} onChange={setRange} />
        </div>
      </div>

      {/* Unattributed-call gap banner (only shows when ≥1% of spend lacks user_id) */}
      <UnattributedBanner days={7} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">{fmt(overview.totals.total_cost)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Daily Cost</p>
                <p className="text-2xl font-bold text-gray-900">{fmt(avgDaily)}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cost / User</p>
                <p className="text-2xl font-bold text-gray-900">{fmt(costPerUser)}</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Calls</p>
                <p className="text-2xl font-bold text-gray-900">{fmtNum(overview.totals.total_calls)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'health' && <LLMHealthMonitor startDate={startDate} endDate={endDate} />}
      {tab === 'overview' && <OverviewTab overview={overview} />}
      {tab === 'reconciliation' && <ReconciliationTab />}
      {tab === 'cache' && <CacheSavingsTab queryParams={queryParams} userFilter={userFilter} />}
      {tab === 'by-call' && byCall && (
        <ByCallTab
          data={byCall}
          sorted={sortedCalls}
          SortHeader={SortHeader}
        />
      )}
      {tab === 'actual-vs-modeled' && comparison && <ActualVsModeledTab data={comparison} />}
      {tab === 'by-profile' && profileData && <ByProfileTab data={profileData} />}
    </div>
  );
};

// ── Overview Tab ─────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{ overview: OverviewData }> = ({ overview }) => (
  <div className="space-y-6">
    {/* Daily Cost Trend */}
    {overview.daily_trend.length > 0 && (
      <Card>
        <CardHeader><CardTitle className="text-lg">Daily Cost Trend</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={overview.daily_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `$${v.toFixed(2)}`} />
              <Tooltip formatter={(v: number) => [`$${v.toFixed(4)}`, 'Cost']} />
              <Area type="monotone" dataKey="cost" stroke="#10B981" fill="#10B98133" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    )}

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* By Model — Pie Chart */}
      {overview.by_model.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Cost by Model</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={overview.by_model}
                  dataKey="total_cost"
                  nameKey="model"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ model, total_cost }) => `${model}: ${fmt(total_cost)}`}
                >
                  {overview.by_model.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => fmt(v, 4)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* By Model — Table */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Model Details</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Calls</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg In</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Out</th>
                </tr>
              </thead>
              <tbody>
                {overview.by_model.map(m => (
                  <tr key={m.model} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{m.model}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.call_count)}</td>
                    <td className="px-3 py-2 text-right">{fmt(m.total_cost, 4)}</td>
                    <td className="px-3 py-2 text-right">{fmt(m.avg_cost_per_call, 6)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.avg_input)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.avg_output)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* By Function */}
    {overview.by_function.length > 0 && (
      <Card>
        <CardHeader><CardTitle className="text-lg">Cost by Function</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={Math.max(200, overview.by_function.length * 36)}>
            <BarChart data={overview.by_function} layout="vertical" margin={{ left: 140 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => `$${v.toFixed(2)}`} />
              <YAxis type="category" dataKey="function_name" tick={{ fontSize: 12 }} width={130} />
              <Tooltip formatter={(v: number) => fmt(v, 4)} />
              <Bar dataKey="total_cost" fill="#10B981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    )}
  </div>
);

// ── By Call Name Tab ─────────────────────────────────────────────────────────

const ByCallTab: React.FC<{
  data: ByCallNameData;
  sorted: CallNameRow[];
  SortHeader: React.FC<{ col: keyof CallNameRow; label: string; className?: string }>;
}> = ({ data, sorted, SortHeader }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg flex items-center">
        Per-Call-Name Cost Breakdown
        <HelpTooltip content="Every distinct call_name tracked in llm_usage_actual, sorted by total cost. Click column headers to re-sort." />
      </CardTitle>
      <p className="text-sm text-gray-500">Total: {fmt(data.total_cost)} across {fmtNum(data.call_names.length)} call names</p>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b">
              <SortHeader col="call_name" label="Call Name" />
              <SortHeader col="call_count" label="Calls" className="text-right" />
              <SortHeader col="unique_users" label="Users" className="text-right" />
              <SortHeader col="total_cost" label="Total Cost" className="text-right" />
              <SortHeader col="avg_cost" label="Avg Cost" className="text-right" />
              <SortHeader col="pct_of_total" label="% Total" className="text-right" />
              <SortHeader col="avg_input" label="Avg In" className="text-right" />
              <SortHeader col="avg_output" label="Avg Out" className="text-right" />
              <SortHeader col="avg_latency" label="Latency" className="text-right" />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.call_name} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">{r.call_name}</td>
                <td className="px-3 py-2 text-right">{fmtNum(r.call_count)}</td>
                <td className="px-3 py-2 text-right">{r.unique_users}</td>
                <td className="px-3 py-2 text-right font-medium">{fmt(r.total_cost, 4)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.avg_cost, 6)}</td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center">
                    {fmtPct(r.pct_of_total)}
                    <span
                      className="ml-1 inline-block h-2 bg-green-500 rounded"
                      style={{ width: `${Math.min(r.pct_of_total, 100) * 0.6}px` }}
                    />
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{fmtNum(r.avg_input)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(r.avg_output)}</td>
                <td className="px-3 py-2 text-right">{r.avg_latency.toFixed(1)}s</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.most_used_model}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

// ── Actual vs Modeled Tab ────────────────────────────────────────────────────

const ActualVsModeledTab: React.FC<{ data: ActualVsModeledData }> = ({ data }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg flex items-center">
        Actual vs V5 Modeled Estimates
        <HelpTooltip content="Compares real per-call costs from production against the estimated token counts stored in llm_call_definitions. Helps validate cost projections." />
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Call Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Calls</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Actual Cost</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actual In</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Modeled In</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actual Out</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Modeled Out</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual Model</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modeled Model</th>
            </tr>
          </thead>
          <tbody>
            {data.comparisons.map(r => {
              const inputDiff = r.estimated_input_tokens > 0
                ? ((r.avg_input_tokens - r.estimated_input_tokens) / r.estimated_input_tokens * 100)
                : null;
              const outputDiff = r.estimated_output_tokens > 0
                ? ((r.avg_output_tokens - r.estimated_output_tokens) / r.estimated_output_tokens * 100)
                : null;

              return (
                <tr key={r.call_name} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-xs">{r.display_name}</div>
                    <div className="font-mono text-xs text-gray-400">{r.call_name}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{r.category}</span>
                  </td>
                  <td className="px-3 py-2 text-right">{fmtNum(r.call_count)}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(r.avg_actual_cost, 6)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(r.avg_input_tokens)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.estimated_input_tokens > 0 ? (
                      <span>
                        {fmtNum(r.estimated_input_tokens)}
                        {inputDiff !== null && (
                          <span className={`ml-1 text-xs ${inputDiff > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            ({inputDiff > 0 ? '+' : ''}{inputDiff.toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    ) : <span className="text-gray-400">--</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{fmtNum(r.avg_output_tokens)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.estimated_output_tokens > 0 ? (
                      <span>
                        {fmtNum(r.estimated_output_tokens)}
                        {outputDiff !== null && (
                          <span className={`ml-1 text-xs ${outputDiff > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            ({outputDiff > 0 ? '+' : ''}{outputDiff.toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    ) : <span className="text-gray-400">--</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.actual_model}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.modeled_model}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.comparisons.length === 0 && (
        <p className="text-center text-gray-500 py-8">No call_name data available for the selected period.</p>
      )}
    </CardContent>
  </Card>
);

// ── By Profile Tab ───────────────────────────────────────────────────────────

const ByProfileTab: React.FC<{ data: ProfileData }> = ({ data }) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      {/* Profile summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.profiles.map(p => (
          <Card key={p.profile_name} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpanded(expanded === p.profile_name ? null : p.profile_name)}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                {p.profile_name}
                <span className="text-xs font-normal text-gray-500">{p.user_count} user{p.user_count !== 1 ? 's' : ''}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Total Cost</p>
                  <p className="font-bold text-lg">{fmt(p.total_cost)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Avg / User</p>
                  <p className="font-bold text-lg">{fmt(p.avg_cost_per_user)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Calls</p>
                  <p className="font-medium">{fmtNum(p.total_calls)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Avg Latency</p>
                  <p className="font-medium">{p.avg_latency.toFixed(1)}s</p>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-400">
                Range: {fmt(p.min_cost_per_user)} – {fmt(p.max_cost_per_user)} per user
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.profiles.length === 0 && (
        <p className="text-center text-gray-500 py-8">No profile assignment data available. Assign users to LLM profiles to see comparisons.</p>
      )}

      {/* Profile comparison bar chart */}
      {data.profiles.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Profile Cost Comparison (Avg/User)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.profiles}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="profile_name" />
                <YAxis tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip formatter={(v: number) => fmt(v, 4)} />
                <Bar dataKey="avg_cost_per_user" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Avg Cost/User" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Expanded call breakdown */}
      {expanded && (() => {
        const profile = data.profiles.find(p => p.profile_name === expanded);
        if (!profile || profile.call_breakdown.length === 0) return null;
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Call Breakdown: {expanded}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Call Name</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Calls</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.call_breakdown.map(cb => (
                      <tr key={cb.call_name} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">{cb.call_name}</td>
                        <td className="px-3 py-2 text-right">{fmtNum(cb.call_count)}</td>
                        <td className="px-3 py-2 text-right">{fmt(cb.total_cost, 4)}</td>
                        <td className="px-3 py-2 text-right">{fmt(cb.avg_cost, 6)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{cb.most_used_model}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

// ── Reconciliation Tab ──────────────────────────────────────────────────────────
//
// Compares each LLM vendor's reported billing against our DB-tracked cost.
// Surfaces the daily delta from vendor_billing_daily (populated by
// /cron/vendor-billing-reconciliation). Vendor-agnostic — adding new vendors
// to services/billing_reconciliation/ automatically appears here.

interface ReconcileVendorSummary {
  vendor: string;
  latest_billing_date: string | null;
  latest_status: string;
  latest_error: string | null;
  latest_vendor_cost: number;
  latest_db_tracked: number;
  latest_delta_pct: number | null;
  rolling_avg_delta_pct: number | null;
  rolling_abs_avg_delta_pct: number | null;
  rolling_days_with_data: number;
  fetched_at: string | null;
  status_indicator: 'ok' | 'drift' | 'broken' | 'not_supported';
}

interface ReconcileSummaryResp { vendors: ReconcileVendorSummary[]; }

interface ReconcileRow {
  billing_date: string | null;
  vendor: string;
  vendor_total_cost_usd: number;
  db_tracked_cost_usd: number;
  delta_usd: number | null;
  delta_pct: number | null;
  fetch_status: string;
  fetch_error: string | null;
}

interface ReconcileHistoryResp { days: number; rows: ReconcileRow[]; }

const STATUS_BADGE: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
  ok:            { bg: 'bg-green-100',  text: 'text-green-800',  icon: <CheckCircle className="h-4 w-4" />,   label: 'Matches' },
  drift:         { bg: 'bg-amber-100',  text: 'text-amber-800',  icon: <AlertTriangle className="h-4 w-4" />, label: 'Drifting' },
  broken:        { bg: 'bg-red-100',    text: 'text-red-800',    icon: <XCircle className="h-4 w-4" />,       label: 'Fetch broken' },
  not_supported: { bg: 'bg-gray-100',   text: 'text-gray-600',   icon: <Info className="h-4 w-4" />,          label: 'Not supported' },
};

const ReconciliationTab: React.FC = () => {
  const [summary, setSummary] = useState<ReconcileSummaryResp | null>(null);
  const [history, setHistory] = useState<ReconcileHistoryResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, historyRes] = await Promise.all([
          apiFetch<ReconcileSummaryResp>(`/api/admin/llm-analytics/billing-reconciliation/summary`),
          apiFetch<ReconcileHistoryResp>(`/api/admin/llm-analytics/billing-reconciliation?days=30`),
        ]);
        if (!cancelled) {
          setSummary(summaryRes);
          setHistory(historyRes);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load reconciliation data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
        <span className="ml-3 text-gray-600">Loading reconciliation data...</span>
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>;
  }
  if (!summary) return null;

  // No data yet (cron hasn't run)
  if (summary.vendors.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Scale className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No reconciliation data yet</p>
            <p className="text-sm text-gray-500 mt-1">
              The daily reconciliation cron runs at 2 AM UTC. After the first run, per-vendor
              comparisons of DB-tracked spend vs vendor-reported billing will appear here.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const historyByVendor: Record<string, ReconcileRow[]> = {};
  if (history) {
    for (const r of history.rows) {
      if (!historyByVendor[r.vendor]) historyByVendor[r.vendor] = [];
      historyByVendor[r.vendor].push(r);
    }
    // Reverse so charts are chronological left→right
    for (const v of Object.keys(historyByVendor)) {
      historyByVendor[v] = historyByVendor[v].slice().reverse();
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            Vendor Billing Reconciliation
            <HelpTooltip content="Daily comparison of each vendor's reported billing vs our DB-tracked LLM cost. Reconciliation cron runs at 2 AM UTC and writes to vendor_billing_daily." />
          </CardTitle>
          <p className="text-sm text-gray-500">Proves the dashboard matches reality. Rolling 7-day average delta per vendor.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {summary.vendors.map(v => {
              const badge = STATUS_BADGE[v.status_indicator] ?? STATUS_BADGE.broken;
              return (
                <div key={v.vendor} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-900 capitalize">{v.vendor}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                      {badge.icon}
                      {badge.label}
                    </span>
                  </div>
                  {v.latest_status === 'success' ? (
                    <>
                      <div className="text-xs text-gray-500 mb-1">Latest day ({v.latest_billing_date})</div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div>
                          <div className="text-gray-500 text-xs">Vendor billed</div>
                          <div className="font-medium">${v.latest_vendor_cost.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">DB tracked</div>
                          <div className="font-medium">${v.latest_db_tracked.toFixed(2)}</div>
                        </div>
                      </div>
                      {v.latest_delta_pct !== null && (
                        <div className={`text-sm font-medium ${Math.abs(v.latest_delta_pct) <= 5 ? 'text-green-700' : Math.abs(v.latest_delta_pct) <= 15 ? 'text-amber-700' : 'text-red-700'}`}>
                          Delta: {v.latest_delta_pct > 0 ? '+' : ''}{v.latest_delta_pct.toFixed(1)}%
                        </div>
                      )}
                      {v.rolling_avg_delta_pct !== null && (
                        <div className="text-xs text-gray-500 mt-1">
                          7-day avg: {v.rolling_avg_delta_pct > 0 ? '+' : ''}{v.rolling_avg_delta_pct.toFixed(1)}% ({v.rolling_days_with_data} days)
                        </div>
                      )}
                    </>
                  ) : v.latest_status === 'not_supported' ? (
                    <p className="text-sm text-gray-500 italic">{v.latest_error || 'Vendor does not expose a usage API.'}</p>
                  ) : v.latest_status === 'no_credentials' ? (
                    <p className="text-sm text-gray-600">{v.latest_error || 'Credentials not configured.'}</p>
                  ) : (
                    <p className="text-sm text-red-700">{v.latest_error || 'Fetch failed.'}</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Per-vendor history + chart for vendors with successful data */}
      {Object.entries(historyByVendor).map(([vendor, rows]) => {
        const chartData = rows
          .filter(r => r.fetch_status === 'success')
          .map(r => ({
            day: r.billing_date?.slice(5) ?? '?',
            'Vendor billed': r.vendor_total_cost_usd,
            'DB tracked': r.db_tracked_cost_usd,
            'Delta %': r.delta_pct ?? 0,
          }));
        if (chartData.length === 0) return null;

        return (
          <Card key={vendor}>
            <CardHeader>
              <CardTitle className="text-base capitalize">{vendor} — 30-day reconciliation history</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => `$${v.toFixed(2)}`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `$${v.toFixed(4)}`} />
                  <Legend />
                  <Bar dataKey="Vendor billed" fill="#10B981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="DB tracked" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Vendor Cost</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">DB Tracked</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Delta</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Delta %</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={`${r.vendor}-${r.billing_date}`} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-1.5 font-mono text-xs">{r.billing_date}</td>
                        <td className="px-3 py-1.5 text-right">${r.vendor_total_cost_usd.toFixed(4)}</td>
                        <td className="px-3 py-1.5 text-right">${r.db_tracked_cost_usd.toFixed(4)}</td>
                        <td className="px-3 py-1.5 text-right">{r.delta_usd !== null ? `$${r.delta_usd.toFixed(4)}` : '—'}</td>
                        <td className={`px-3 py-1.5 text-right font-medium ${r.delta_pct === null ? 'text-gray-400' : Math.abs(r.delta_pct) <= 5 ? 'text-green-700' : Math.abs(r.delta_pct) <= 15 ? 'text-amber-700' : 'text-red-700'}`}>
                          {r.delta_pct !== null ? `${r.delta_pct > 0 ? '+' : ''}${r.delta_pct.toFixed(1)}%` : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-600">{r.fetch_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};


// ── Cache Savings (2026-10-06) ───────────────────────────────────────────────
// OpenAI bills a repeated prompt prefix at the cached-input rate. The ledger records
// the cached share per call from 2026-10-06 (call_llm + streamed main chat).

interface CacheSavingsResp {
  period_days: number;
  captured_since: string | null;
  totals: {
    calls: number;
    input_tokens: number;
    cached_tokens: number;
    cache_hit_rate_pct: number;
    list_input_cost: number;
    input_cost: number;
    saved: number;
    saved_pct_of_input: number;
    total_cost: number;
  };
  uncaptured: { calls: number; total_cost: number };
  daily: { day: string; list_input_cost: number; input_cost: number; saved: number; cache_hit_rate_pct: number }[];
  by_call: { call_name: string; model: string; calls: number; cache_hit_rate_pct: number; saved: number; total_cost: number }[];
}

const CacheSavingsTab: React.FC<{ queryParams: string; userFilter: UserFilter }> = ({ queryParams, userFilter }) => {
  const [data, setData] = useState<CacheSavingsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<CacheSavingsResp>(`/api/admin/subscription-analytics/cache-savings?${queryParams}&user_filter=${userFilter}`)
      .then(r => { if (!cancelled) setData(r); })
      .catch((e: any) => { if (!cancelled) setError(e.message ?? 'Failed to load cache savings'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [queryParams, userFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
        <span className="ml-3 text-gray-600">Loading cache savings...</span>
      </div>
    );
  }
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>;
  if (!data) return null;

  const t = data.totals;
  const chart = data.daily.map(d => ({
    day: d.day.slice(5),
    'Full input price': d.list_input_cost,
    'Actually billed': d.input_cost,
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            Prompt-cache discount
            <HelpTooltip content="OpenAI automatically serves the repeated start of a prompt (1,024+ tokens) from its cache and bills those tokens at the cached-input rate — e.g. GPT-6 Luna $0.01 instead of $0.10 per million. Nothing to switch on; this page shows how much it is saving." />
          </CardTitle>
          <p className="text-sm text-gray-500">
            {data.captured_since
              ? `Recorded per call since ${data.captured_since.slice(0, 10)}. Days before that show no cache data — not "no discount".`
              : 'No calls have recorded cache data yet. It starts with the first OpenAI call after this release deploys.'}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Input served from cache</p>
              <p className="text-lg font-bold">{t.cache_hit_rate_pct.toFixed(1)}%</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Saved in period</p>
              <p className="text-lg font-bold text-green-700">${t.saved.toFixed(2)}</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Input spend saved</p>
              <p className="text-lg font-bold">{t.saved_pct_of_input.toFixed(1)}%</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Calls measured</p>
              <p className="text-lg font-bold">{t.calls.toLocaleString()}</p>
            </div>
          </div>
          {data.uncaptured.calls > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              {data.uncaptured.calls.toLocaleString()} calls in this period (${data.uncaptured.total_cost.toFixed(2)}) carry no cache data —
              recorded before this release, by another vendor, or by a path that tracks usage by hand. They are left out of the rates above.
            </p>
          )}
        </CardContent>
      </Card>

      {chart.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Input cost per day — full price vs billed</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `$${Number(v).toFixed(2)}`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `$${v.toFixed(4)}`} />
                <Legend />
                <Bar dataKey="Full input price" fill="#9CA3AF" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Actually billed" fill="#1F6C4F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {data.by_call.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By call — where the cache helps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Call</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Calls</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">From cache</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Saved</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Billed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_call.map(r => (
                    <tr key={r.call_name} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-mono text-xs">{r.call_name}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-600">{r.model}</td>
                      <td className="px-3 py-1.5 text-right">{r.calls.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right">{r.cache_hit_rate_pct.toFixed(1)}%</td>
                      <td className="px-3 py-1.5 text-right text-green-700">${r.saved.toFixed(4)}</td>
                      <td className="px-3 py-1.5 text-right">${r.total_cost.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
